import { html, LitElement, nothing, type PropertyValues, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { keyed } from 'lit/directives/keyed.js';
import styles from './ui-volatility-rating.css?inline';

const CName = 'ui-volatility-rating';

/** Default number of steps on the volatility scale. */
const DEFAULT_STEPS = 5;

/**
 *
 * @prop {number} value - Number of lit steps (0–max).
 * @prop {number} max - Total number of steps. Defaults to 5.
 * @prop {string} iconSrc - URL for the volatility icon image.
 * @prop {string} label - Accessible name for the rating. When set, the
 *   rating is exposed to assistive tech as an image with this name; when
 *   empty (default) it stays decorative (`aria-hidden`), for cases where an
 *   adjacent visible label or control already conveys the value.
 */
@customElement(CName)
export class UIVolatilityRating extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: Number }) value = 0;
  @property({ type: Number }) max = DEFAULT_STEPS;
  @property({ type: String }) iconSrc = '';
  @property({ type: String }) label = '';

  /** True only after an interactive change, so the pulse never fires on first
   *  render — the rating is also shown statically on result cards. */
  private _pulse = false;

  protected willUpdate(changed: PropertyValues<this>): void {
    // Pulse the newly-active bolt only when the value actually changed from a
    // prior render (not on initial mount).
    if (changed.has('value') && changed.get('value') !== undefined) {
      this._pulse = true;
    }
  }

  render() {
    if (!this.iconSrc) return nothing;

    // The last lit bolt is the "active" one that pulses on change.
    const activeIndex = this.value - 1;
    // With a label, expose the rating as a single image to AT (the individual
    // bolt <img>s stay alt="" so only the composed name is announced). Without
    // one, the whole rating is decorative — an adjacent label or control
    // already conveys the value.
    const labelled = this.label !== '';

    return html`
      <div
        class="volatility-rating"
        role=${labelled ? 'img' : nothing}
        aria-label=${labelled ? this.label : nothing}
        aria-hidden=${labelled ? nothing : 'true'}
      >
        ${Array.from({ length: this.max }, (_, i) => {
          const lit = i < this.value;
          const isActive = i === activeIndex;
          const icon = html`
            <img
              class=${classMap({
                'volatility-icon': true,
                'volatility-icon--muted': !lit,
                'volatility-icon--pulse': isActive && this._pulse,
              })}
              src=${this.iconSrc}
              alt=""
            />
          `;
          // Key the active bolt on the value so it remounts on change and the
          // CSS pulse animation replays. Other bolts render normally.
          return isActive ? keyed(this.value, icon) : icon;
        })}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIVolatilityRating;
  }
}
